// THE single source of truth: (brand x role) -> ordered NavSection[].
//
// Each cell is a builder that takes the modifiers the resolver computed and
// returns an explicit ordered section list — NO isFlow/isBallee ternaries (the
// brand is already chosen by which cell you index).
//
// One invariant is enforced structurally (not by convention) so no cell can
// silently drop or duplicate a universal entry — the failure class behind the
// director-missing-Home and super-admin-missing-Home bugs:
//   • `standaloneHome(m)` emits a Home link ONLY when the cell does not compose
//     the dancer surfaces (whose first group — Today / Community — already
//     leads with a Home leaf). Every cell therefore surfaces exactly one Home,
//     never zero, never two. Client-user cells carry their own Home
//     (clientHomeFlow → /home/client-user) and opt out of this helper.
// `nav-invariants.test.ts` asserts the invariant across every
// (brand x role x modifier) combination.
//
// 2026-07-05 consolidation: the Account group and the dancer "My Profile"
// group are NO LONGER sidebar sections. They live in the footer user
// dropdown (ProfileAccountDropdownContainer → PersonalAccountDropdown
// `menuGroups`) and in the mobile menu sheet, keeping the sidebar to the
// work surfaces: Home/Today · Inbox · Community/Pipeline · Opportunities ·
// [Selling] · [Competitions] · [Admin]. The client portal is the `client`
// acting role's OWN menu (the clientUser cells) — it never composes into
// another role's cell (Phase A, 2026-07-05 proposal §1).
//
// Super-admin cells layer Favorites + a collapsed Admin-tools section on top of
// the brand's user surfaces, structurally identical across brands (only the
// user surfaces differ, which the brand-specific cell already lists). That
// symmetry is the whole point of the re-architecture.
//
// Ordering note: dividers are intentionally NOT emitted here. The `divider`
// section kind exists in the vocabulary for future cosmetic use without
// re-touching the schema.

import type { Brand } from '~/lib/brand';
import {
  adminToolsSection,
  clientHomeFlow,
  clientPortalGroupsFlow,
  competitionsPartnerBallee,
  dancerCommunityBallee,
  dancerMoneyFlow,
  dancerOpportunitiesBallee,
  dancerPipelineFlow,
  dancerTodayFlow,
  dividerSection,
  favoritesSection,
  homeSection,
  inboxSection,
  sellingSection,
  type SectionModifiers,
} from './nav-sections';
import type { NavRole, NavSection } from './nav-types';

export type MatrixCellOptions = SectionModifiers;

type CellBuilder = (m: MatrixCellOptions) => NavSection[];

// ── Universal-frame helpers (enforce the per-cell invariants) ────────────────

/**
 * Every cell leads with the SAME primary cluster — Home + Inbox as bare rows,
 * then a divider — before its labeled sections. This is the ONE place Home and
 * Inbox live, so no cell buries Home inside its first labeled group (the
 * lone-Inbox fix, 2026-07-13; matches Linear / Asana / Slite). It also keeps
 * the "exactly one Home, one Inbox" invariant true structurally
 * (nav-invariants.test.ts). Client cells pass their own Home target
 * (`clientHomeFlow` → /home/client-user). The trailing divider is dropped when
 * the cell has no sections after the cluster (a bare Home + Inbox shell).
 */
const withPrimaryCluster = (
  rest: NavSection[],
  home: NavSection = homeSection,
): NavSection[] =>
  rest.length > 0
    ? [home, inboxSection, dividerSection, ...rest]
    : [home, inboxSection];

// ── Dancer user surfaces per brand (shared by dancer + super-admin cells) ────

function flowDancerSurfaces(m: MatrixCellOptions): NavSection[] {
  return [
    dancerTodayFlow,
    dancerPipelineFlow(m),
    dancerMoneyFlow(m),
    ...(m.isSeller ? [sellingSection] : []),
  ];
}

function balleeDancerSurfaces(m: MatrixCellOptions): NavSection[] {
  return [
    dancerCommunityBallee(m),
    dancerOpportunitiesBallee,
    ...(m.isSeller ? [sellingSection] : []),
  ];
}

// Each cell below lists ONLY its role-specific sections; the shared primary
// cluster (Home + Inbox + divider) is prepended by `withPrimaryCluster`, so
// Home and Inbox can never drift per cell and Inbox is never an orphan row.

// ── FLOW cells ───────────────────────────────────────────────────────────────

// Non-admin dancer (Flow): [Home · Inbox] · Today · Pipeline · Money ·
// [Competitions]. Reached when the user IS a dancer OR is neither dancer nor
// client (a bare account shell → just Home + Inbox), including an org member
// who directs a competition but holds no organizer account — they reach it
// from the composed Competitions group (resolve-roles.ts: every cell composes
// from `partnerCompetitions`; the `/competitions/*` leaves are a cross-brand
// surface shown on Flow too — route-brand.config.ts "Competitions in the Flow
// partner menu"). The client portal never composes here — `client` is its own
// acting role (Phase A, 2026-07-05 proposal §1).
const flowDancer: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    ...(m.isDancer ? flowDancerSurfaces(m) : []),
    ...(competitions ? [competitions] : []),
  ]);
};

// Client acting role (Flow): [Client Home · Inbox] · Client Portal groups ·
// [Competitions]. A client who also directs a competition keeps their
// Competitions group (mirror of balleeClientUser).
const flowClientUser: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster(
    [...clientPortalGroupsFlow(), ...(competitions ? [competitions] : [])],
    clientHomeFlow,
  );
};

// Organizer (Flow): [Home · Inbox] · [Competitions if also a partner] ·
// [Dancer surfaces if also a dancer]. The org *workspace* lives in the
// team-account nav, not here — this cell is the organizer's personal shell.
const flowOrganizer: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    ...(competitions ? [competitions] : []),
    ...(m.isDancer ? flowDancerSurfaces(m) : []),
  ]);
};

// Super admin (Flow): [Home · Inbox] · Favorites · Admin-tools ·
// [Dancer surfaces] · [Competitions]. Structurally symmetric with
// balleeSuperAdmin (only the brand's user surfaces differ). Home + Inbox still
// LEAD (via the primary cluster) so the daily entry points don't sink below
// the ~27 admin rows (2026-07-05 wall-of-items feedback).
const flowSuperAdmin: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    favoritesSection,
    adminToolsSection,
    ...(m.isDancer ? flowDancerSurfaces(m) : []),
    ...(competitions ? [competitions] : []),
  ]);
};

// ── BALLEE cells ──────────────────────────────────────────────────────────────

// Non-admin dancer (Ballee): [Home · Inbox] · Community · Opportunities ·
// [Competitions]. Bare shell (not a dancer) → just Home + Inbox.
const balleeDancer: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    ...(m.isDancer ? balleeDancerSurfaces(m) : []),
    ...(competitions ? [competitions] : []),
  ]);
};

// Pure client user (Ballee): [Home · Inbox] · [Competitions]. No client portal
// on Ballee (clientUserRoutes is Flow-gated). Home targets /home (the default
// cluster Home); competition partners still get their portal group.
const balleeClientUser: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster(competitions ? [competitions] : []);
};

// Organizer (Ballee): [Home · Inbox] · [Competitions if also a partner] ·
// [Dancer surfaces if also a dancer]. Mirror of flowOrganizer with Ballee
// dancer surfaces.
const balleeOrganizer: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    ...(competitions ? [competitions] : []),
    ...(m.isDancer ? balleeDancerSurfaces(m) : []),
  ]);
};

// Super admin (Ballee): [Home · Inbox] · Favorites · Admin-tools ·
// [Dancer surfaces] · [Competitions]. Structurally symmetric with flowSuperAdmin.
const balleeSuperAdmin: CellBuilder = (m) => {
  const competitions = competitionsPartnerBallee(m);
  return withPrimaryCluster([
    favoritesSection,
    adminToolsSection,
    ...(m.isDancer ? balleeDancerSurfaces(m) : []),
    ...(competitions ? [competitions] : []),
  ]);
};

export const MATRIX: Record<Brand, Record<NavRole, CellBuilder>> = {
  flow: {
    dancer: flowDancer,
    clientUser: flowClientUser,
    organizer: flowOrganizer,
    superAdmin: flowSuperAdmin,
  },
  ballee: {
    dancer: balleeDancer,
    clientUser: balleeClientUser,
    organizer: balleeOrganizer,
    superAdmin: balleeSuperAdmin,
  },
};
